import { useEffect } from 'preact/hooks';
import type { ComponentChildren } from 'preact';
import type { Slot, Stats } from '../../../data/schema';
import { loadContent } from '../../../data/content';
import { statEffect } from '../../../core/stats';
import { hudState } from '../state';
import { DERIVED_LABEL, ELEMENT_LABEL, STAT_LABEL, fr, mult } from '../derivedText';

const content = loadContent();
const SLOTS: Slot[] = ['arme', 'coiffe', 'cape', 'amulette', 'anneau', 'bottes'];
const SLOT_LABEL: Record<Slot, string> = { arme: 'Arme', coiffe: 'Coiffe', cape: 'Cape', amulette: 'Amulette', anneau: 'Anneau', bottes: 'Bottes' };
const STAT_ORDER: (keyof Stats)[] = ['vitalite', 'force', 'intelligence', 'agilite', 'chance', 'sagesse'];

/** Vrai si la cible de l'événement est un champ de saisie (la touche C doit alors rester un caractère). */
function isTyping(e: KeyboardEvent): boolean {
  const el = (e.target instanceof HTMLElement ? e.target : document.activeElement) as HTMLElement | null;
  if (!el) return false;
  return el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable;
}

/** Touche C : ouvre ou ferme le panneau de caractéristiques, seulement pendant une run. */
export function useStatsHotkey(inRun: boolean): void {
  useEffect(() => {
    if (!inRun) return undefined;
    const onKey = (e: KeyboardEvent): void => {
      if (e.code !== 'KeyC' || e.repeat || e.ctrlKey || e.metaKey || e.altKey || isTyping(e)) return;
      hudState.statsOpen.value = !hudState.statsOpen.value;
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [inRun]);
}

const Pic = ({ name }: { name: string }) => <img class="stat-pic" src={`/assets/icons/stats/${name}.png`} alt="" />;
/** Portée : trajectoire pointillée vers une cible (pas de picto dédié dans le client Retro). */
const RangePic = () => (
  <svg class="stat-pic" viewBox="0 0 32 32" aria-hidden="true" focusable="false">
    <circle cx="23" cy="9" r="6.5" fill="#f4eed8" stroke="#3a2a16" stroke-width="2" /><circle cx="23" cy="9" r="2.4" fill="#c8322b" />
    <path d="M4 28 C 8 20, 12 15, 18 12" fill="none" stroke="#3a2a16" stroke-width="2.4" stroke-dasharray="3 3" stroke-linecap="round" />
  </svg>
);
/** Vitesse des projectiles : flèche avec traînées (pas de picto dédié dans le client Retro). */
const ProjectileSpeedPic = () => (
  <svg class="stat-pic" viewBox="0 0 32 32" aria-hidden="true" focusable="false">
    <path d="M3 12h7M3 20h5M5 16h10" stroke="#3a2a16" stroke-width="2.2" stroke-linecap="round" />
    <path d="M12 16h13" stroke="#3a2a16" stroke-width="3" stroke-linecap="round" /><path d="M21 9l8 7-8 7z" fill="#ffa040" stroke="#3a2a16" stroke-width="1.6" stroke-linejoin="round" />
  </svg>
);
/** Projectiles : trois billes de Feu. */
const ProjectilesPic = () => (
  <svg class="stat-pic" viewBox="0 0 32 32" aria-hidden="true" focusable="false">
    <circle cx="9" cy="10" r="4.5" fill="#ffa040" stroke="#3a2a16" stroke-width="1.8" /><circle cx="22" cy="14" r="4.5" fill="#ffa040" stroke="#3a2a16" stroke-width="1.8" /><circle cx="12" cy="23" r="4.5" fill="#ffa040" stroke="#3a2a16" stroke-width="1.8" />
  </svg>
);

function Row({ pic, name, value, raw }: { pic: ComponentChildren; name: string; value: string; raw?: string }) {
  return (
    <li class="stat-row">
      {pic}
      <span class="stat-name">{name}</span>
      <b class="stat-value">{value}</b>
      {raw && <small class="stat-raw">{raw}</small>}
    </li>
  );
}

function effectText(stat: keyof Stats, value: number): string {
  const e = statEffect(stat, value);
  if (e.kind === 'hp') return `+${e.amount} PV max`;
  if (e.kind === 'xp') return `+${e.pct} % XP`;
  return `+${e.pct} % dégâts ${ELEMENT_LABEL[e.element]}`;
}

export function StatsPanel() {
  const s = hudState;
  const primary = s.primary.value;
  const stats = s.stats.value;
  const d = s.derived.value;
  const equipment = s.equipment.value;
  const close = (): void => { s.statsOpen.value = false; };
  return (
    <div class="panel stats-panel" role="dialog" aria-label="Caractéristiques">
      <header class="stats-head">
        <h2>Caractéristiques</h2>
        <button class="stats-x" aria-label="Fermer les caractéristiques" onMouseDown={(e) => e.preventDefault()} onClick={close}>×</button>
      </header>
      {d && (
        <ul class="stats-list stats-run" aria-label="Stats de run">
          <Row pic={<Pic name="pa" />} name={DERIVED_LABEL.attackSpeed} value={mult(d.attackSpeed.mult)} raw={`${fr(d.attackSpeed.shotsPerSec)} tirs/s`} />
          {d.damage && <Row pic={<Pic name={`degats-${d.damage.element}`} />} name={DERIVED_LABEL.damage} value={`${d.damage.min}-${d.damage.max}`} raw={`${mult(d.damage.mult)} ${ELEMENT_LABEL[d.damage.element]}`} />}
          {d.projectileSpeed && <Row pic={<ProjectileSpeedPic />} name={DERIVED_LABEL.projectileSpeed} value={mult(d.projectileSpeed.mult)} raw={`${Math.round(d.projectileSpeed.pxPerSec)} px/s`} />}
          <Row pic={<Pic name="pm" />} name={DERIVED_LABEL.moveSpeed} value={mult(d.moveSpeed.mult)} raw={`${Math.round(d.moveSpeed.pxPerSec)} px/s`} />
          {d.range && <Row pic={<RangePic />} name={DERIVED_LABEL.range} value={mult(d.range.mult)} raw={`${Math.round(d.range.px)} px`} />}
          {d.projectiles && <Row pic={<ProjectilesPic />} name={DERIVED_LABEL.projectiles} value={`${d.projectiles.count}`} raw={d.projectiles.pierce > 0 ? `perfore ${d.projectiles.pierce}` : 'sans perforation'} />}
          <Row pic={<Pic name="coeur" />} name="PV" value={`${Math.ceil(s.hp.value)} / ${d.hpMax}`} />
          <Row pic={<Pic name="sagesse" />} name={DERIVED_LABEL.xpMult} value={mult(d.xpMult)} raw={`niveau ${s.level.value}, ${s.xp.value} / ${s.xpNext.value} XP`} />
        </ul>
      )}
      <h3>Caractéristiques Dofus</h3>
      <ul class="stats-list stats-dofus" aria-label="Caractéristiques Dofus">
        {STAT_ORDER.map((k) => <Row key={k} pic={<Pic name={k} />} name={STAT_LABEL[k]} value={`${stats[k]}`} raw={effectText(k, stats[k])} />)}
      </ul>
      {primary && <p class="stats-primary">Sort principal : <b>{primary.name}</b> (niv. {primary.level}){s.autohit.value && <>, <b>Autohit</b> {s.autohit.value === 'on' ? 'actif' : 'en attente d’une cible'}</>}</p>}
      <h3>Équipement</h3>
      <dl class="stats-grid">
        {SLOTS.map((slot) => {
          const id = equipment[slot];
          return <><dt key={`${slot}-l`}>{SLOT_LABEL[slot]}</dt><dd key={`${slot}-v`} class={id ? '' : 'stats-empty'}>{id ? (content.items[id]?.name ?? id) : 'vide'}</dd></>;
        })}
      </dl>
      <button class="stats-close" aria-label="Fermer les caractéristiques" onMouseDown={(e) => e.preventDefault()} onClick={close}>Fermer</button>
    </div>
  );
}
